import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarBlank, ArrowRight, MapPin } from "@phosphor-icons/react";
import { useLanguage } from "@/i18n/LanguageContext";
import { countdownLabel, countdownTier } from "@/lib/dates";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INTL_LOCALE = {
  en: "en-US", es: "es", pt: "pt-BR", fr: "fr", de: "de", nl: "nl", he: "he", zh: "zh-CN",
};

function shortRange(startISO, endISO, loc) {
  const s = new Date(startISO + "T12:00:00");
  const e = new Date(endISO + "T12:00:00");
  if (startISO === endISO) return s.toLocaleDateString(loc, { month: "short", day: "numeric" });
  if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear())
    return `${s.toLocaleDateString(loc, { month: "short", day: "numeric" })}\u2013${e.getDate()}`;
  return `${s.toLocaleDateString(loc, { month: "short", day: "numeric" })} \u2013 ${e.toLocaleDateString(loc, { month: "short", day: "numeric" })}`;
}

export default function EventsTeaser({ theme = "light" }) {
  const { lang } = useLanguage();
  const loc = INTL_LOCALE[lang] || "en-US";
  const [events, setEvents] = useState([]);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/events`)
      .then((r) => alive && setEvents((r.data.events || []).slice(0, 3)))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  if (!events.length) return null;
  const dark = theme === "dark";

  return (
    <section
      data-testid="events-teaser"
      className={`${dark ? "bg-emerald text-sand" : "bg-sand-2"} py-20 md:py-28`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <p className={`overline mb-4 flex items-center gap-2 ${dark ? "text-gold" : "text-sunset"}`}>
              <CalendarBlank size={16} weight="fill" /> What&apos;s on
            </p>
            <h2 className={`font-display text-4xl md:text-6xl leading-[0.95] tracking-tight max-w-xl ${dark ? "text-sand" : "text-emerald"}`}>
              Time your stay with the fun.
            </h2>
          </div>
          <Link
            to="/events"
            data-testid="events-teaser-all"
            className={`group inline-flex items-center gap-2 transition-colors duration-300 whitespace-nowrap ${dark ? "text-sand hover:text-gold" : "text-emerald hover:text-sunset"}`}
          >
            See the full calendar <ArrowRight size={18} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-5 md:gap-6">
          {events.map((ev, i) => (
            <motion.div
              key={ev.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              data-testid={`teaser-event-${ev.id}`}
              className={`rounded-2xl p-6 md:p-7 border transition-colors duration-300 ${dark ? "border-sand/15 bg-emerald hover:border-gold/50" : "border-emerald/10 bg-sand hover:border-sunset/40"}`}
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold ${dark ? "bg-sand/10 text-gold" : "bg-sunset/12 text-sunset"}`}>
                  {shortRange(ev.start, ev.end, loc)}
                </span>
                <span
                  data-testid={`teaser-countdown-${ev.id}`}
                  className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold ${
                    countdownTier(ev.start) === "near"
                      ? "bg-sunset text-sand"
                      : countdownTier(ev.start) === "mid"
                      ? dark ? "bg-gold/20 text-gold" : "bg-gold/25 text-emerald"
                      : dark ? "bg-sand/10 text-sand/60" : "bg-emerald/8 text-emerald-soft"
                  }`}
                >
                  {countdownLabel(ev.start)}
                </span>
              </div>
              <h3 className={`font-display text-xl md:text-2xl leading-snug mt-3 mb-2 ${dark ? "text-sand" : "text-emerald"}`}>
                {ev.title}
              </h3>
              <p className={`flex items-center gap-1.5 text-sm ${dark ? "text-sand/70" : "text-emerald-soft"}`}>
                <MapPin size={13} weight="fill" className={dark ? "text-gold" : "text-sunset"} /> {ev.location}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
